import { useLogger } from 'common';
import { useEffect } from 'react';
import { Outlet, RouteObject, useLocation, useNavigate, useRoutes } from 'react-router-dom';

import { ROUTE_PATHNAMES, ROUTE_PATHS } from './routeConstants';
import { useAppSync } from '../contexts/AppSyncContext';
import { useFirmwareContext } from '../contexts/FirmwareContext';
import {
  LockScreen,
  LockScreenPinCode,
  Dashboard,
  Home,
  Water,
  HVAC,
  Air,
  Electrical,
  Settings,
  WiFiConnectionSettings,
  LockScreenSettings,
  ServiceContactSettings,
  GeneralSettings,
  Weather,
  SetPinScreen,
  ResetPinScreen,
  WifiConnectScreen,
  ScreenSaver,
} from '../screens';
import { InitialLockScreen } from '../screens/Lock_InitialConfiguration';
import { NoInternetLockScreen } from '../screens/Lock_NoInternet';
import { SomethingWrongLockScreen } from '../screens/Lock_SomethingWrong';
import { DisablePinScreen } from '../screens/Pin_DisablePin';
import { BacklightSettings } from '../screens/Settings_Backlight';

export const Router = () => {
  const { iotStatus, iotStatusLoading, isLocked, iotStatusError, isIdle } = useFirmwareContext();
  const { sensors } = useAppSync();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const logger = useLogger('Router');

  const routes: RouteObject[] = [
    {
      path: ROUTE_PATHNAMES.lock,
      element: <Outlet />,
      children: [
        {
          path: ROUTE_PATHNAMES.lock,
          element: <LockScreen />,
        },
        {
          path: ROUTE_PATHNAMES.lockPinCode,
          element: <LockScreenPinCode />,
        },
        {
          path: ROUTE_PATHNAMES.lockNoInternet,
          element: <NoInternetLockScreen onUnlock={() => navigate(ROUTE_PATHNAMES.wifi)} />,
        },
        {
          path: ROUTE_PATHNAMES.lockInitialConfiguration,
          element: <InitialLockScreen onUnlock={() => navigate(ROUTE_PATHNAMES.lockPinCode)} />,
        },
        {
          path: ROUTE_PATHNAMES.lockSomethingWrong,
          element: (
            <SomethingWrongLockScreen
              onReload={() => window.location.reload()}
              onContactUs={() => navigate(ROUTE_PATHNAMES.contact)}
            />
          ),
        },
        {
          path: ROUTE_PATHNAMES.screenSaver,
          element: <ScreenSaver />,
        },
      ],
    },
    {
      path: `${ROUTE_PATHNAMES.dashboard}/*`,
      element: <Dashboard />,
      children: [
        {
          index: true,
          element: <Home />,
        },
        {
          path: ROUTE_PATHS.water,
          element: <Water />,
        },
        {
          path: ROUTE_PATHS.hvac,
          element: <HVAC />,
        },
        {
          path: ROUTE_PATHS.air,
          element: <Air />,
        },
        {
          path: ROUTE_PATHS.energy,
          element: <Electrical />,
        },
        {
          path: ROUTE_PATHS.weather,
          element: <Weather />,
        },
        {
          path: `${ROUTE_PATHS.settings}/*`,
          element: <Settings />,
          children: [
            {
              index: true,
              element: <GeneralSettings />,
            },
            {
              path: ROUTE_PATHS.lockScreen,
              element: <LockScreenSettings />,
            },

            {
              path: ROUTE_PATHS.wifi,
              element: <WiFiConnectionSettings />,
            },
            {
              path: ROUTE_PATHS.backlight,
              element: <BacklightSettings />,
            },
            {
              path: ROUTE_PATHS.contact,
              element: <ServiceContactSettings />,
            },
            {
              path: ROUTE_PATHS.setPin,
              element: <SetPinScreen />,
            },
            {
              path: ROUTE_PATHS.resetPin,
              element: <ResetPinScreen />,
            },
            {
              path: ROUTE_PATHS.disablePin,
              element: <DisablePinScreen />,
            },
            {
              path: ROUTE_PATHS.connectWifi + '/:networkName',
              element: <WifiConnectScreen />,
            },
          ],
        },
      ],
    },
  ];

  const lockNavigation = () => {
    const isProvisioned = iotStatus?.provisioned || false;
    const isConnected = iotStatus?.connected || false;

    if (!isProvisioned) {
      const allowed = [ROUTE_PATHNAMES.contact];
      const target = allowed.includes(pathname) ? pathname : ROUTE_PATHNAMES.lockSomethingWrong;
      logger.debug('Not provisioned, navigating to:', { target });
      navigate(target, { replace: true });
      return;
    }

    if (!isConnected) {
      const allowed = [ROUTE_PATHNAMES.wifi];
      const target = allowed.includes(pathname) ? pathname : ROUTE_PATHNAMES.lockNoInternet;
      logger.debug('Not connected, navigating to:', { target });
      navigate(target, { replace: true });
      return;
    }

    const allowed = [
      ROUTE_PATHNAMES.lock,
      ROUTE_PATHNAMES.lockInitialConfiguration,
      ROUTE_PATHNAMES.lockPinCode,
    ];

    const target = allowed.includes(pathname) ? pathname : ROUTE_PATHNAMES.lock;
    logger.debug('Lock navigation, navigating to:', { target });
    navigate(target, { replace: true });
  };

  const dashboardNavigation = () => {
    const isLockedRoute = [
      ROUTE_PATHNAMES.lock,
      ROUTE_PATHNAMES.lockInitialConfiguration,
      ROUTE_PATHNAMES.lockPinCode,
      ROUTE_PATHNAMES.lockNoInternet,
      ROUTE_PATHNAMES.lockSomethingWrong,
      ROUTE_PATHNAMES.screenSaver,
    ].includes(pathname);
    const isNoRoute = pathname === '/';
    const targetPathname = isLockedRoute || isNoRoute ? ROUTE_PATHNAMES.dashboard : pathname;

    logger.debug('Dashboard navigation, navigating to:', { targetPathname });
    navigate({ pathname: targetPathname, search }, { replace: true });
  };

  useEffect(() => {
    if (isIdle) {
      navigate(ROUTE_PATHNAMES.screenSaver, { replace: true });
      return;
    }

    if (iotStatusError || iotStatusLoading) {
      logger.debug('Loading status or error detected, navigating to lockSomethingWrong');
      if (pathname === ROUTE_PATHNAMES.contact) {
        logger.debug('Allowing access to contact page');
        return;
      }
      navigate(ROUTE_PATHNAMES.lockSomethingWrong, { replace: true });
      return;
    }

    if (sensors.length === 0) {
      logger.debug('No sensors detected, navigating to lockInitialConfiguration');
      navigate(ROUTE_PATHNAMES.lockInitialConfiguration, { replace: true });
      return;
    }

    if (isLocked) {
      lockNavigation();
    } else {
      dashboardNavigation();
    }
  }, [isLocked, isIdle, pathname, search, iotStatus, navigate, sensors]);

  return useRoutes(routes);
};
